import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Bell, Check, X, Inbox, CheckCheck, Loader2, ExternalLink, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtAbsolute(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
}

const KIND_META = {
  approval:           { color: '#22C55E', bg: '#DCFCE7', Icon: Check, label: 'Approved' },
  rejection:          { color: '#DC2626', bg: '#FEE2E2', Icon: X,     label: 'Rejected' },
  info:               { color: '#1F4FBE', bg: '#E0E7FF', Icon: Bell,  label: 'Info' },
  bug_report:         { color: '#DC2626', bg: '#FEE2E2', Icon: Bell,  label: 'New bug report' },
  bug_status_update:  { color: '#D97706', bg: '#FEF3C7', Icon: Bell,  label: 'Bug status' },
  bug_assigned:       { color: '#1F4FBE', bg: '#E0E7FF', Icon: Bell,  label: 'Bug assigned' },
  bug_reply:          { color: '#1F4FBE', bg: '#E0E7FF', Icon: Bell,  label: 'Bug reply' },
  assignment:         { color: '#1F4FBE', bg: '#E0E7FF', Icon: Bell,  label: 'Task assigned' },
};

function fmtRelative(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

/* User-facing notification inbox.
   - Polls /api/notifications every 30s while signed in
   - Bell shows a red badge with unread count
   - Clicking opens a popover panel showing the latest items
   - Click an item to mark it read; "Mark all read" clears the badge

   Visible to every signed-in role (editor / engineer / admin) — viewers
   never reach the admin panel where this lives.
*/
export default function NotificationBell({ token }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const popRef = useRef(null);

  const fetchItems = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(Array.isArray(data) ? data : []);
    } catch { /* ignore — keep last known */ }
  }, [token]);

  // Poll every 30s
  useEffect(() => {
    if (!token) return undefined;
    fetchItems();
    const t = setInterval(fetchItems, 30000);
    return () => clearInterval(t);
  }, [token, fetchItems]);

  // Click-outside to close
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const unreadCount = items.filter((x) => !x.is_read).length;

  const markRead = async (n) => {
    if (busy) return;
    setBusy(true);
    if (!n.is_read) {
      setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, is_read: true } : x));
      try {
        await axios.post(`${API}/notifications/${n.id}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, is_read: false } : x));
      }
    }
    setBusy(false);
  };

  const onItemClick = (n) => {
    // If this notification has a deep-link target (Approvals request or
    // bug report), clicking the row should NAVIGATE directly — not just
    // expand inline. That matches what users expect ("I clicked the
    // notification, take me there"). For pure info notifications
    // without a target, we keep the legacy toggle-expand behaviour so
    // the body text can be read in full.
    if (!n.is_read) markRead(n);
    const link = detailLink(n);
    if (link) {
      // Build the fake event so handleDeepLinkClick can decide between
      // pushState (admin already on /admin/dashboard) vs. full nav.
      const fakeEvent = { preventDefault: () => {} };
      const onAdmin = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin/dashboard');
      setOpen(false);
      if (onAdmin) {
        handleDeepLinkClick(fakeEvent, link.href);
      } else if (typeof window !== 'undefined') {
        window.location.href = link.href;
      }
      return;
    }
    // No deep-link → fall back to the inline expand/collapse behaviour.
    setExpandedId((prev) => (prev === n.id ? null : n.id));
  };

  // Deep-link for kind-specific entities. When admins click a bug-related
  // notification they jump straight to the bug in the admin dashboard.
  // For permission-related notifications (request_id present), the link
  // points to the Approvals tab with the row pre-highlighted so the
  // admin can approve/deny without re-finding the request.
  // NOTE: the admin dashboard route is `/admin/dashboard` — `/admin` is
  // the AuthGate.
  const detailLink = (n) => {
    if (n.bug_id) return { href: `/admin/dashboard?bug=${encodeURIComponent(n.bug_id)}`, label: 'Open bug report' };
    if (n.request_id) return {
      href: `/admin/dashboard?tab=approvals&request=${encodeURIComponent(n.request_id)}`,
      label: 'Open in Approvals',
    };
    // iter-110 — Fallback: notifications whose `kind` indicates an
    // approval/rejection event but lack an explicit request_id should
    // still take you to the Approvals tab so the click never feels dead.
    const approvalKinds = new Set(['approval', 'rejection', 'approval_pending', 'change_request']);
    if (n.kind && approvalKinds.has(n.kind)) {
      return { href: `/admin/dashboard?tab=approvals`, label: 'Open Approvals' };
    }
    if (n.kind === 'access_request' || n.kind === 'permission') {
      return { href: `/admin/dashboard?tab=access`, label: 'Open Access' };
    }
    return null;
  };

  // If the admin is ALREADY on /admin/dashboard, the anchor's default
  // navigation wouldn't re-trigger our tab-change effect (same-origin
  // same-path). We intercept the click, push the new URL via
  // history.pushState, and dispatch `atrium:admin-deeplink` so
  // AdminDashboard switches tab + PermissionRequestsPanel scrolls/
  // highlights the right row.
  const handleDeepLinkClick = (e, href) => {
    if (typeof window === 'undefined') return;
    const onAdmin = window.location.pathname.startsWith('/admin/dashboard');
    if (!onAdmin) return; // let the browser navigate normally
    e.preventDefault();
    try {
      window.history.pushState({}, '', href);
      window.dispatchEvent(new CustomEvent('atrium:admin-deeplink', { detail: { href } }));
      setOpen(false);
    } catch { /* ignore — fallback to default nav */ }
  };

  const markAllRead = async () => {
    if (busy || !unreadCount) return;
    setBusy(true);
    const snapshot = items;
    setItems((prev) => prev.map((x) => ({ ...x, is_read: true })));
    try {
      await axios.post(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      setItems(snapshot);
    } finally {
      setBusy(false);
    }
  };

  if (!token) return null;

  return (
    <div className="relative" ref={popRef}>
      <button
        type="button"
        data-testid="notification-bell"
        onClick={() => setOpen((v) => !v)}
        title={unreadCount ? `${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}` : 'Notifications'}
        className="relative flex items-center justify-center w-8 h-8 rounded-full transition-colors"
        style={{
          backgroundColor: unreadCount > 0 ? '#1F4FBE15' : 'transparent',
          color: unreadCount > 0 ? '#1F4FBE' : '#4A5D5D',
        }}
      >
        <Bell className="w-4 h-4" />
        {unreadCount > 0 && (
          <span
            data-testid="notification-bell-count"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center text-[9px] font-bold"
            style={{ backgroundColor: '#1F4FBE', color: 'white', fontFamily: 'Manrope' }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-popover"
          className="absolute top-full right-0 mt-2 w-[340px] max-w-[92vw] rounded-xl shadow-2xl overflow-hidden border z-[100]"
          style={{ backgroundColor: 'white', borderColor: '#A3D4D440' }}
        >
          <div className="px-4 py-3 flex items-center justify-between border-b" style={{ borderColor: '#EAE5D9' }}>
            <h3 className="text-sm font-bold" style={{ color: '#0A1A2F', fontFamily: 'Manrope' }}>
              Notifications {unreadCount > 0 && (
                <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#1F4FBE', color: 'white' }}>
                  {unreadCount}
                </span>
              )}
            </h3>
            {unreadCount > 0 && (
              <button
                type="button"
                data-testid="notification-mark-all-read"
                disabled={busy}
                onClick={markAllRead}
                className="flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded transition-colors disabled:opacity-50"
                style={{ color: '#1F4FBE', fontFamily: 'Manrope' }}
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCheck className="w-3 h-3" />} Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {!items.length ? (
              <div className="px-6 py-10 text-center" data-testid="notification-empty">
                <Inbox className="w-10 h-10 mx-auto mb-2 opacity-30" style={{ color: '#A3D4D4' }} />
                <p className="text-xs" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                  No notifications yet. You'll get one when an admin or engineer reviews your change request.
                </p>
              </div>
            ) : (
              items.map((n) => {
                const meta = KIND_META[n.kind] || KIND_META.info;
                const Icon = meta.Icon;
                const expanded = expandedId === n.id;
                const link = detailLink(n);
                return (
                  <div
                    key={n.id}
                    data-testid={`notification-item-${n.id}`}
                    className="border-b transition-colors"
                    style={{
                      borderColor: '#EAE5D9',
                      backgroundColor: n.is_read ? 'white' : '#F0F4FA',
                    }}
                  >
                    <button
                      type="button"
                      data-testid={`notification-item-${n.id}-toggle`}
                      onClick={() => onItemClick(n)}
                      className="w-full text-left px-4 py-3 flex gap-2.5 transition-colors hover:bg-[#FAF8F3]"
                    >
                      <div
                        className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: meta.bg }}
                      >
                        <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        {(n.title || n.kind) && (
                          <p
                            className="text-[11px] font-bold leading-snug mb-0.5"
                            style={{ color: meta.color, fontFamily: 'Manrope' }}
                          >
                            {n.title || meta.label}
                          </p>
                        )}
                        <p
                          className={`text-xs leading-snug whitespace-pre-wrap ${expanded ? '' : 'line-clamp-2'}`}
                          style={{ color: '#1A2E2E', fontFamily: 'Manrope', fontWeight: n.is_read ? 400 : 600 }}
                        >
                          {n.body || n.message || '(No message body)'}
                        </p>
                        <p className="text-[10px] mt-1" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                          {fmtRelative(n.created_at)}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        {!n.is_read && (
                          <span
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ backgroundColor: '#1F4FBE' }}
                            aria-label="Unread"
                          />
                        )}
                        <ChevronRight
                          className="w-3 h-3 transition-transform"
                          style={{ color: '#7A8C8C', transform: expanded ? 'rotate(90deg)' : 'none' }}
                        />
                      </div>
                    </button>
                    {expanded && (
                      <div
                        data-testid={`notification-item-${n.id}-details`}
                        className="px-4 pb-3 pt-1 ml-[42px] space-y-1.5 text-[11px]"
                        style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}
                      >
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                          <span><strong style={{ color: '#0A1A2F' }}>Type:</strong> {meta.label}</span>
                          <span><strong style={{ color: '#0A1A2F' }}>Received:</strong> {fmtAbsolute(n.created_at)}</span>
                          {n.bug_id && <span><strong style={{ color: '#0A1A2F' }}>Bug ID:</strong> <code className="font-mono">{String(n.bug_id).slice(0, 8)}…</code></span>}
                          {n.request_id && <span><strong style={{ color: '#0A1A2F' }}>Request ID:</strong> <code className="font-mono">{String(n.request_id).slice(0, 8)}…</code></span>}
                        </div>
                        {link && (
                          <a
                            href={link.href}
                            data-testid={`notification-item-${n.id}-link`}
                            onClick={(e) => handleDeepLinkClick(e, link.href)}
                            className="inline-flex items-center gap-1 text-[11px] font-bold underline"
                            style={{ color: meta.color }}
                          >
                            <ExternalLink className="w-3 h-3" />
                            {link.label}
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
